import React, { useMemo, useState } from "react";
import {
  useListAdminUsers,
  useInviteStaff,
  useUpdateAdminUser,
  useListStaffInvitations,
  getListAdminUsersQueryKey,
  getListStaffInvitationsQueryKey,
  type UserRole,
  type UserStatus,
  type CurrentUser,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/hooks/use-toast";
import { Plus, Loader2, Info, Ban, RotateCcw } from "lucide-react";

// Internal staff roles only. Agents, master dealers and their reps are managed
// through onboarding / the Users & Roles matrix, not this staff directory.
const INTERNAL_ROLES: UserRole[] = [
  "admin",
  "finance",
  "operations",
  "account_manager",
  "poc_support",
];

const ROLE_LABELS: Record<UserRole, string> = {
  agent: "Agent",
  master_dealer: "Master Dealer",
  account_manager: "Account Manager",
  admin: "Admin",
  finance: "Finance",
  support: "Support",
  poc_support: "PoC Support",
  operations: "Operations",
};

const PAGE_SIZE = 20;

function apiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "data" in err) {
    const data = (err as { data?: unknown }).data;
    if (data && typeof data === "object" && "error" in data) {
      const message = (data as { error?: unknown }).error;
      if (typeof message === "string" && message.trim()) return message;
    }
  }
  return fallback;
}

function initials(user: CurrentUser): string {
  const first = user.firstName?.charAt(0) ?? "";
  const last = user.lastName?.charAt(0) ?? "";
  const combined = `${first}${last}`.trim();
  return combined || user.fullName.charAt(0).toUpperCase() || "U";
}

function fmtDate(value: string): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-ZA", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
}

export function UserManagement() {
  const [page, setPage] = useState(1);
  const [roleFilter, setRoleFilter] = useState<UserRole | "all">("all");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [pendingId, setPendingId] = useState<number | null>(null);

  const queryClient = useQueryClient();
  const { toast } = useToast();
  const updateUser = useUpdateAdminUser();

  const params = {
    page,
    pageSize: PAGE_SIZE,
    ...(roleFilter !== "all" ? { role: roleFilter } : {}),
  };
  const { data, isLoading, error } = useListAdminUsers(params);
  const {
    data: invitationsData,
    isLoading: invitationsLoading,
    error: invitationsError,
  } = useListStaffInvitations();
  const invitations = invitationsData?.items ?? [];

  const users = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Page-local count of active admins. The server enforces the authoritative
  // "last active admin" guard; this just lets us pre-disable the obvious case
  // so an admin isn't surprised by a 403.
  const activeAdmins = useMemo(
    () =>
      users.filter(
        (u) => u.role === "admin" && (u.status ?? "active") === "active",
      ),
    [users],
  );

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: getListAdminUsersQueryKey() });

  const isLastActiveAdmin = (user: CurrentUser) =>
    user.role === "admin" &&
    (user.status ?? "active") === "active" &&
    activeAdmins.length <= 1;

  const handleRoleChange = (user: CurrentUser, role: UserRole) => {
    if (role === user.role) return;
    setPendingId(user.id);
    updateUser.mutate(
      { id: user.id, data: { role } },
      {
        onSuccess: () => {
          invalidate();
          toast({
            title: "Role updated",
            description: `${user.fullName} is now ${ROLE_LABELS[role]}.`,
          });
        },
        onError: (err) => {
          toast({
            variant: "destructive",
            title: "Couldn't update role",
            description: apiErrorMessage(err, "Please try again."),
          });
        },
        onSettled: () => setPendingId(null),
      },
    );
  };

  const handleStatusToggle = (user: CurrentUser) => {
    const next: UserStatus =
      (user.status ?? "active") === "suspended" ? "active" : "suspended";
    setPendingId(user.id);
    updateUser.mutate(
      { id: user.id, data: { status: next } },
      {
        onSuccess: () => {
          invalidate();
          toast({
            title:
              next === "suspended"
                ? "Account suspended"
                : "Account reactivated",
            description: `${user.fullName} has been ${
              next === "suspended" ? "suspended" : "reactivated"
            }.`,
          });
        },
        onError: (err) => {
          toast({
            variant: "destructive",
            title: "Couldn't update status",
            description: apiErrorMessage(err, "Please try again."),
          });
        },
        onSettled: () => setPendingId(null),
      },
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0B132B]">Staff</h1>
          <p className="text-sm text-gray-500 mt-1">
            Invite internal team members and manage their roles and access.
          </p>
        </div>
        <Button
          onClick={() => setInviteOpen(true)}
          className="bg-[#E6007E] hover:bg-[#c4006b] text-white"
        >
          <Plus className="w-4 h-4 mr-2" /> Invite staff
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <Label className="text-sm text-gray-600">Filter by role</Label>
        <Select
          value={roleFilter}
          onValueChange={(v) => {
            setRoleFilter(v as UserRole | "all");
            setPage(1);
          }}
        >
          <SelectTrigger className="w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All staff</SelectItem>
            {INTERNAL_ROLES.map((r) => (
              <SelectItem key={r} value={r}>
                {ROLE_LABELS[r]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg text-[#0B132B]">
            Staff directory
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 animate-pulse">
              <div className="h-8 bg-gray-200 w-1/4 rounded mb-4"></div>
              <div className="h-48 bg-gray-200 rounded"></div>
            </div>
          ) : error ? (
            <div className="p-6 text-red-500">Failed to load staff.</div>
          ) : (
            <>
              <Table>
                <TableHeader className="bg-gray-50">
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => {
                    const status = user.status ?? "active";
                    const rowPending = pendingId === user.id;
                    const lockLastAdmin = isLastActiveAdmin(user);
                    return (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarFallback className="bg-[#0B132B] text-white text-xs">
                                {initials(user)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-medium text-[#0B132B]">
                              {user.fullName}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {user.email}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={user.role}
                            onValueChange={(v) =>
                              handleRoleChange(user, v as UserRole)
                            }
                            disabled={rowPending || lockLastAdmin}
                          >
                            <SelectTrigger className="w-44">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {INTERNAL_ROLES.map((r) => (
                                <SelectItem key={r} value={r}>
                                  {ROLE_LABELS[r]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={status} />
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleStatusToggle(user)}
                            disabled={rowPending || lockLastAdmin}
                            className={
                              status === "suspended"
                                ? "border-gray-200 text-[#00A859] hover:text-[#00A859] hover:border-[#00A859]"
                                : "border-gray-200 text-red-600 hover:text-red-600 hover:border-red-300"
                            }
                          >
                            {rowPending ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : status === "suspended" ? (
                              <>
                                <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                                Reactivate
                              </>
                            ) : (
                              <>
                                <Ban className="w-3.5 h-3.5 mr-1.5" />
                                Suspend
                              </>
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {users.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="text-center p-8 text-gray-500"
                      >
                        No staff found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <div className="flex items-center justify-between p-4 border-t border-gray-100">
                <span className="text-sm text-gray-500">
                  {total} {total === 1 ? "staff member" : "staff members"}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="border-gray-200"
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-gray-500">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className="border-gray-200"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg text-[#0B132B]">
            Pending invitations
          </CardTitle>
          <p className="text-sm text-gray-500">
            People who have been invited but haven&apos;t signed up yet. They
            join the directory automatically once they create an account with
            the invited email.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          {invitationsLoading ? (
            <div className="p-6 animate-pulse">
              <div className="h-8 bg-gray-200 w-1/4 rounded mb-4"></div>
              <div className="h-24 bg-gray-200 rounded"></div>
            </div>
          ) : invitationsError ? (
            <div className="p-6 text-red-500">
              Failed to load invitations.
            </div>
          ) : (
            <Table>
              <TableHeader className="bg-gray-50">
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Invited</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invitations.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-medium text-[#0B132B]">
                      {inv.email}
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {ROLE_LABELS[inv.role]}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={inv.status} />
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {fmtDate(inv.createdAt)}
                    </TableCell>
                  </TableRow>
                ))}
                {invitations.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="text-center p-8 text-gray-500"
                    >
                      No pending invitations.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
        <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#0B132B]" />
        <span>
          The last active admin cannot be demoted or suspended. Assign another
          admin first.
        </span>
      </div>

      <InviteStaffDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </div>
  );
}

function InviteStaffDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<UserRole>("operations");
  const inviteStaff = useInviteStaff();

  const reset = () => {
    setEmail("");
    setRole("operations");
  };

  const submit = async () => {
    if (!email.trim()) return;
    try {
      const result = await inviteStaff.mutateAsync({
        data: { email: email.trim(), role },
      });
      queryClient.invalidateQueries({ queryKey: getListAdminUsersQueryKey() });
      queryClient.invalidateQueries({
        queryKey: getListStaffInvitationsQueryKey(),
      });
      if (result.delivered) {
        toast({
          title: "Invitation sent",
          description: `${email.trim()} has been invited as ${ROLE_LABELS[role]}.`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Saved, but the email wasn't delivered",
          description: `${email.trim()} won't receive an invite link yet.${
            result.deliveryError ? ` Reason: ${result.deliveryError}` : ""
          }`,
        });
      }
      reset();
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Could not send invitation",
        description: apiErrorMessage(err, "Please try again."),
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[460px] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-[#0B132B]">Invite staff</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Email address</Label>
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              placeholder="name@mooya.co.za"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select value={role} onValueChange={(v) => setRole(v as UserRole)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INTERNAL_ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              The invitee receives this role automatically when they sign up
              with this email.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={submit}
            disabled={!email.trim() || inviteStaff.isPending}
            className="bg-[#E6007E] hover:bg-[#c4006b] text-white rounded-xl w-full"
          >
            {inviteStaff.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Send invitation"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
